/* eslint-disable no-underscore-dangle */
((ns) => {
  const {
    logError,
    errorTemplate,
    renderRangeSlider,
    renderSortBy,
    createRefinementListWithHeader,
    createGroupedNumericRefinementList,
    createExpressTagRefinement,
    renderCurrentRefinements,
    openFirstFiveFacets,
    renderPagination,
    renderStats,
    createHierarchicalMenuWithHeader,
  } = ns;

  const createSearchInstance = async (config = {}) => {
    try {
      if (!window.algoliasearch || !window.instantsearch) {
        throw new Error("Required search dependencies not loaded");
      }

      // Determine page type (moved up for use in search client analytics tagging)
      let pageType = "default";
      if (window.location.pathname.includes("/search") || config.useVirtualSearchBox) {
        pageType = "search";
      } else if (window.location.pathname.includes("/collections") || !!config.filters) {
        pageType = "collection";
      }

      // ——— Custom search client with analytics tagging (PP-756) ———
      const algoliaClient = window.algoliasearch.algoliasearch(
        window.algoliaConfig.appId || "{{ settings.algolia_app_id }}",
        window.algoliaConfig.apiKey || "{{ settings.algolia_api_key }}"
      );
      const searchClient = {
        ...algoliaClient,
        search(requests) {
          if (!requests || requests.length === 0) {
            return algoliaClient.search(requests);
          }

          const modifiedRequests = requests.map((request) => {
            const query = request.params?.query || "";
            const isEmptyQuery = query.trim().length === 0;

            // Tag requests for Algolia Analytics segmentation
            if (pageType === "collection") {
              return {
                ...request,
                params: {
                  ...request.params,
                  analytics: true,
                  clickAnalytics: true,
                  analyticsTags: isEmptyQuery
                    ? ["category-browsing"]
                    : ["category-search"],
                },
              };
            }

            // Non-collection pages: exclude initial empty queries, tag typed searches
            if (isEmptyQuery) {
              return {
                ...request,
                params: {
                  ...request.params,
                  analytics: false,
                },
              };
            }

            return {
              ...request,
              params: {
                ...request.params,
                analytics: true,
                clickAnalytics: true,
                analyticsTags: ["typed-search"],
              },
            };
          });

          return algoliaClient.search(modifiedRequests);
        },
      };

      // Get search query from URL parameters if useUrlQuery is enabled
      const urlParams = new URLSearchParams(window.location.search);
      const initialQuery = config.useUrlQuery ? urlParams.get("q") || "" : "";

      // Build initial UI state
      const initialUiState = config.useUrlQuery
        ? {
            [window.algoliaConfig.indexName]: {
              query: initialQuery,
            },
          }
        : undefined;

      // Create routing configuration for SEO-friendly URLs
      const routing = {
        stateMapping: {
          stateToRoute(uiState) {
            const indexUiState = uiState[window.algoliaConfig.indexName] || {};
            const route = {};

            // Add query parameter
            if (indexUiState.query) {
              route.q = indexUiState.query;
            }

            // Add sort parameter
            if (indexUiState.sortBy && indexUiState.sortBy !== window.algoliaConfig.indexName) {
              // Simplify sort parameter names
              const sortMap = {
                [`${window.algoliaConfig.indexName}_bestsellers`]: "bestsellers",
                [`${window.algoliaConfig.indexName}_title_asc`]: "title-asc",
                [`${window.algoliaConfig.indexName}_title_desc`]: "title-desc",
                [`${window.algoliaConfig.indexName}_price_asc`]: "price-asc",
                [`${window.algoliaConfig.indexName}_price_desc`]: "price-desc",
              };
              route.sort = sortMap[indexUiState.sortBy] || indexUiState.sortBy;
            }

            // Add price range - check multiple possible state locations
            if (indexUiState.range && indexUiState.range.price) {
              const priceRange = indexUiState.range.price;
              // Handle string format with colon separator
              if (typeof priceRange === "string" && priceRange.includes(":")) {
                const [min, max] = priceRange.split(":");
                route.price = `${min || ""}-${max || ""}`;
              }
              // Handle object format (just in case)
              else if (typeof priceRange === "object") {
                if (
                  (priceRange.min !== undefined && priceRange.min !== null) ||
                  (priceRange.max !== undefined && priceRange.max !== null)
                ) {
                  route.price = `${priceRange.min !== undefined ? priceRange.min : ""}-${priceRange.max !== undefined ? priceRange.max : ""}`;
                }
              }
            } else if (indexUiState.numericMenu && indexUiState.numericMenu.price) {
              const priceRange = indexUiState.numericMenu.price;
              if (
                (priceRange.min !== undefined && priceRange.min !== null) ||
                (priceRange.max !== undefined && priceRange.max !== null)
              ) {
                route.price = `${priceRange.min !== undefined ? priceRange.min : ""}-${priceRange.max !== undefined ? priceRange.max : ""}`;
              }
            }

            // Add refinement list filters
            if (indexUiState.refinementList) {
              // Handle collections
              if (indexUiState.refinementList.collections) {
                route.collections = indexUiState.refinementList.collections.join(",");
              }

              // Handle vendor
              if (indexUiState.refinementList.vendor) {
                route.vendor = indexUiState.refinementList.vendor.join(",");
              }

              // Handle product_type
              if (indexUiState.refinementList.product_type) {
                route["product-type"] = indexUiState.refinementList.product_type.join(",");
              }

              // Handle other attributes with simplified names
              Object.keys(indexUiState.refinementList).forEach((attribute) => {
                // Skip already handled attributes
                if (attribute === "collections" || attribute === "vendor" || attribute === "product_type") {
                  return;
                }

                let simplifiedName = "";

                if (attribute.startsWith("meta.custom.weitere_informationen.")) {
                  simplifiedName = attribute
                    .replace("meta.custom.weitere_informationen.", "")
                    .toLowerCase()
                    .replace(/\s+/g, "-")
                    .replace(/[^\w-]/g, "");
                } else if (attribute.startsWith("meta.custom.")) {
                  simplifiedName = attribute.replace("meta.custom.", "").toLowerCase().replace(/_/g, "-");
                }

                if (simplifiedName && indexUiState.refinementList[attribute].length > 0) {
                  route[simplifiedName] = indexUiState.refinementList[attribute].join(",");
                }
              });
            }

            // Add page number if not first page
            if (indexUiState.page && indexUiState.page > 1) {
              route.page = indexUiState.page;
            }

            return route;
          },

          routeToState(routeState) {
            const state = {
              [window.algoliaConfig.indexName]: {},
            };

            // Restore query
            if (routeState.q) {
              state[window.algoliaConfig.indexName].query = routeState.q;
            }

            // Restore sort
            if (routeState.sort) {
              const sortMap = {
                bestsellers: `${window.algoliaConfig.indexName}_bestsellers`,
                "title-asc": `${window.algoliaConfig.indexName}_title_asc`,
                "title-desc": `${window.algoliaConfig.indexName}_title_desc`,
                "price-asc": `${window.algoliaConfig.indexName}_price_asc`,
                "price-desc": `${window.algoliaConfig.indexName}_price_desc`,
              };
              state[window.algoliaConfig.indexName].sortBy = sortMap[routeState.sort] || window.algoliaConfig.indexName;
            }

            // Restore price range
            if (routeState.price) {
              const [minStr, maxStr] = routeState.price.split("-");

              // Build the colon-separated format that the range widget expects
              let priceRangeStr = "";
              if (minStr || maxStr) {
                priceRangeStr = `${minStr || ""}:${maxStr || ""}`;
              }

              if (priceRangeStr) {
                state[window.algoliaConfig.indexName].range = {
                  price: priceRangeStr,
                };
              }
            }

            // Restore refinement lists
            state[window.algoliaConfig.indexName].refinementList = {};

            // Handle collections
            if (routeState.collections) {
              state[window.algoliaConfig.indexName].refinementList.collections = routeState.collections.split(",");
            }

            // Handle vendor
            if (routeState.vendor) {
              state[window.algoliaConfig.indexName].refinementList.vendor = routeState.vendor.split(",");
            }

            // Handle product_type
            if (routeState["product-type"]) {
              state[window.algoliaConfig.indexName].refinementList.product_type = routeState["product-type"].split(",");
            }

            // Handle other attributes - need to map back to full attribute names
            const attributeMap = {
              // Meta custom attributes
              "express-tag": "meta.custom.express_tag",

              // Weitere Informationen attributes
              "anschluss-in-volt": "meta.custom.weitere_informationen.Anschluss in Volt",
              "anzahl-kochstellen": "meta.custom.weitere_informationen.Anzahl Kochstellen",
              "anzahl-tueren": "meta.custom.weitere_informationen.Anzahl Türen",
              aufkantung: "meta.custom.weitere_informationen.Aufkantung",
              "breite-in-mm": "meta.custom.weitere_informationen.Breite in mm",
              "durchmesser-des-schneidmessers": "meta.custom.weitere_informationen.Durchmesser des Schneidmessers",
              enthaerter: "meta.custom.weitere_informationen.Enthärter",
              "gn-mass": "meta.custom.weitere_informationen.GN Maß",
              geraetetyp: "meta.custom.weitere_informationen.Gerätetyp",
              grundboden: "meta.custom.weitere_informationen.Grundboden",
              "hoehe-in-mm": "meta.custom.weitere_informationen.Höhe in mm",
              kochart: "meta.custom.weitere_informationen.Kochart",
              "korbmasse-in-mm": "meta.custom.weitere_informationen.Korbmaße in mm",
              kuehlbereich: "meta.custom.weitere_informationen.Kühlbereich",
              kuehlung: "meta.custom.weitere_informationen.Kühlung",
              "kuehlvolumen-liter": "meta.custom.weitere_informationen.Kühlvolumen [Liter]",
              laugenpumpe: "meta.custom.weitere_informationen.Laugenpumpe",
              material: "meta.custom.weitere_informationen.Material",
              montageform: "meta.custom.weitere_informationen.Montageform",
              spuelbeckenanzahl: "meta.custom.weitere_informationen.Spülbeckenanzahl",
              "tiefe-in-mm": "meta.custom.weitere_informationen.Tiefe in mm",
              tuerart: "meta.custom.weitere_informationen.Türart",
              "chair-type": "meta.custom.weitere_informationen.chair_type",
              klappbar: "meta.custom.weitere_informationen.klappbar",
              serie: "meta.custom.weitere_informationen.Serie",
              spuelmaschinenart: "meta.custom.weitere_informationen.spuelmaschinenart",
              stapelfaehig: "meta.custom.weitere_informationen.stapelfähig",
              tischform: "meta.custom.weitere_informationen.tischform",
            };

            Object.keys(routeState).forEach((key) => {
              if (attributeMap[key]) {
                state[window.algoliaConfig.indexName].refinementList[attributeMap[key]] = routeState[key].split(",");
              }
            });

            // Restore page
            if (routeState.page) {
              state[window.algoliaConfig.indexName].page = Number(routeState.page);
            }

            return state;
          },
        },
      };

      const search = window.instantsearch({
        indexName: window.algoliaConfig.indexName,
        searchClient: searchClient,
        routing,
        ...(initialUiState && { initialUiState }),
        onStateChange: ({ uiState, setUiState }) => {
          try {
            setUiState(uiState);
            const target = document.querySelector(".collection-products__wrapper");
            if (!target) return; // element doesn't exist
            const targetTop = target.offsetTop; // element's distance from top
            const scrollTop = window.scrollY;   // current scroll distance
            if (scrollTop > targetTop) {
              window.scrollTo({
                top: targetTop,
                behavior: "smooth"
              });
            }           
          } catch (error) {
            logError(error, "onStateChange");
          }
        },
        clickAnalytics: true,
      });

      const customRangeSlider = window.instantsearch.connectors.connectRange(renderRangeSlider);
      const customSortBy = window.instantsearch.connectors.connectSortBy(renderSortBy);
      const customStats = window.instantsearch.connectors.connectStats(renderStats);

      const widgets = [];

      // Add virtual search box if enabled
      if (config.useVirtualSearchBox) {
        const virtualSearchBox = window.instantsearch.connectors.connectSearchBox(() => {});
        widgets.push(virtualSearchBox());
      }

      // Add configure widget if filters are provided
      if (config.filters) {
        widgets.push(
          window.instantsearch.widgets.configure({
            filters: config.filters,
            hitsPerPage: 20, // Set to 12 to ensure pagination is triggered
          }),
        );
      } else {
        // Add default configuration for search pages
        widgets.push(
          window.instantsearch.widgets.configure({
            hitsPerPage: 20, // Set to 12 to ensure pagination is triggered
          }),
        );
      }

      // Add current refinements widget
      const customCurrentRefinements =
        window.instantsearch.connectors.connectCurrentRefinements(renderCurrentRefinements);
      widgets.push(
        customCurrentRefinements({
          container: "#current-refinements",
        }),
      );

      // Add stats widget to update product count
      widgets.push(customStats());

      // Add common widgets
      widgets.push(
        window.instantsearch.widgets.hits({
          container: "#hits",
          templates: {
            item: (hit, { sendEvent }) => {
              // Check current view mode
              const currentView = localStorage.getItem("searchViewMode") || "grid";
              const template = currentView === "list" ? ns.hitTemplateList(hit) : ns.hitTemplate(hit);

              // Add click event tracking and resolve energy label GIDs
              setTimeout(() => {
                const hitElement = document.querySelector(`[data-objectid="${hit.objectID}"]`);
                if (hitElement) {
                  hitElement.addEventListener("click", () => {
                    sendEvent("click", hit, "Product Clicked");
                  });
                }
                // Resolve any pending energy label GIDs in the DOM
                if (ns.resolveEnergyLabelGids) {
                  ns.resolveEnergyLabelGids();
                }
              }, 0);

              return template;
            },
            empty: (results) => {
              // Check if there are active filters/facets
              const hasDisjunctiveFacets =
                results._state.disjunctiveFacetsRefinements &&
                Object.keys(results._state.disjunctiveFacetsRefinements).some(
                  (key) => results._state.disjunctiveFacetsRefinements[key].length > 0,
                );
              const hasNumericRefinements =
                results._state.numericRefinements &&
                Object.keys(results._state.numericRefinements).some(
                  (key) =>
                    results._state.numericRefinements[key] &&
                    Object.keys(results._state.numericRefinements[key]).length > 0,
                );
              const hasHierarchicalFacets =
                results._state.hierarchicalFacetsRefinements &&
                Object.keys(results._state.hierarchicalFacetsRefinements).some(
                  (key) => results._state.hierarchicalFacetsRefinements[key].length > 0,
                );

              const hasActiveFilters = hasDisjunctiveFacets || hasNumericRefinements || hasHierarchicalFacets;

              // When there are active filters in empty state, ensure filter widgets are visible
              if (hasActiveFilters) {
                const currentRefinements = document.querySelector("#current-refinements");

                if (currentRefinements) {
                  currentRefinements.style.display = "block";
                }

                if (window.innerWidth <= 768) {
                  const filterHeader = document.querySelector(".filter-header");
                  const button = filterHeader?.querySelector(".filter-sorting-btn");
                  const filterDrawer = document.querySelector(".filter-drawer");
                  const facetsMobile = document.querySelector("#facets-mobile");

                  // Remove filter-header manipulation - it's handled by renderStats now
                  if (button) {
                    button.style.display = "block";
                  }
                  if (filterDrawer) {
                    filterDrawer.style.display = "block";
                  }
                  if (facetsMobile) {
                    facetsMobile.style.display = "block";
                  }
                }
              }

              return errorTemplate(undefined, undefined, pageType, hasActiveFilters);
            },
          },
          transformItems: (items) => {
            try {
              return items.filter((item) => item && item.handle);
            } catch (error) {
              logError(error, "transformItems");
              return [];
            }
          },
        }),
        // Add pagination widget - only if container exists
        ...(document.querySelector("#pagination")
          ? [
              window.instantsearch.connectors.connectPagination(renderPagination)({
                container: "#pagination",
                padding: 2,
              }),
            ]
          : []),
        // Add bottom pagination widget - only if container exists
        ...(document.querySelector("#pagination-bottom")
          ? [
              window.instantsearch.connectors.connectPagination(renderPagination)({
                container: "#pagination-bottom",
                padding: 2,
              }),
            ]
          : []),
        customSortBy({
          container: "#sorting",
          items: [
            { value: window.algoliaConfig.indexName, label: window.searchTranslations.sort.recommended },
            {
              value: `${window.algoliaConfig.indexName}_bestsellers`,
              label: window.searchTranslations.sort.bestsellers,
            },
            { value: `${window.algoliaConfig.indexName}_title_asc`, label: window.searchTranslations.sort.titleAsc },
            { value: `${window.algoliaConfig.indexName}_title_desc`, label: window.searchTranslations.sort.titleDesc },
            { value: `${window.algoliaConfig.indexName}_price_asc`, label: window.searchTranslations.sort.priceAsc },
            { value: `${window.algoliaConfig.indexName}_price_desc`, label: window.searchTranslations.sort.priceDesc },
          ],
        }),
        // Add mobile sort widget
        customSortBy({
          container: "#sorting-mobile",
          items: [
            { value: window.algoliaConfig.indexName, label: window.searchTranslations.sort.recommended },
            {
              value: `${window.algoliaConfig.indexName}_bestsellers`,
              label: window.searchTranslations.sort.bestsellers,
            },
            { value: `${window.algoliaConfig.indexName}_title_asc`, label: window.searchTranslations.sort.titleAsc },
            { value: `${window.algoliaConfig.indexName}_title_desc`, label: window.searchTranslations.sort.titleDesc },
            { value: `${window.algoliaConfig.indexName}_price_asc`, label: window.searchTranslations.sort.priceAsc },
            { value: `${window.algoliaConfig.indexName}_price_desc`, label: window.searchTranslations.sort.priceDesc },
          ],
        }),
        // Dynamic facet widget generation
        // Create widget factories for all known facets
        window.instantsearch.widgets.dynamicWidgets({
          container: "#facets",
          facets: ["*"], // Request all facets from Algolia
          transformItems: (items) => items,
          widgets: [
            // Price range slider
            (container) => customRangeSlider({ container, attribute: "price" }),

            // Core facets
            (container) =>
              createRefinementListWithHeader({
                container,
                attribute: "collections",
                label:
                  window.FacetMetadata?.getFacetTranslation("collections") ||
                  window.searchTranslations?.filters?.collections ||
                  "Collections",
                searchable: true,
              }),
            (container) =>
              createRefinementListWithHeader({
                container,
                attribute: "vendor",
                label:
                  window.FacetMetadata?.getFacetTranslation("manufacturer") ||
                  window.searchTranslations?.filters?.manufacturer ||
                  "Manufacturer",
              }),
            (container) =>
              createRefinementListWithHeader({
                container,
                attribute: "product_type",
                label:
                  window.FacetMetadata?.getFacetTranslation("productType") ||
                  window.searchTranslations?.filters?.productType ||
                  "Product Type",
              }),

            // Special facets
            (container) =>
              createExpressTagRefinement({
                container,
                attribute: "meta.custom.express_tag",
                label:
                  window.FacetMetadata?.getFacetTranslation("expressShipping") ||
                  window.searchTranslations?.filters?.expressShipping ||
                  "Express Shipping",
              }),
            (container) =>
              createHierarchicalMenuWithHeader({
                container,
                attribute: "meta.custom.item_categories",
                label: window.FacetMetadata?.getFacetTranslation("kategorien") || "Kategorien",
                limit: 1000,
                showMore: true,
                showMoreLimit: 2000,
              }),

            // Generate widgets for all weitere_informationen attributes
            ...(window.FacetMetadata
              ? (() => {
                  const metadataEntries = Object.entries(window.FacetMetadata.FACET_METADATA).filter(([key]) =>
                    key.startsWith("meta.custom.weitere_informationen"),
                  );

                  return metadataEntries.map(([attribute, metadata]) => (container) => {
                    const label = window.FacetMetadata.getFacetTranslation(metadata.translationKey);

                    if (metadata.type === "grouped_numeric") {
                      return createGroupedNumericRefinementList({
                        container,
                        attribute,
                        label,
                      });
                    }

                    return createRefinementListWithHeader({
                      container,
                      attribute,
                      label,
                    });
                  });
                })()
              : []),
          ],
          // No fallbackWidget needed - we've defined all known facets
        }),
        // Add mobile facets - using the same widget factories
        window.instantsearch.widgets.dynamicWidgets({
          container: "#facets-mobile",
          facets: ["*"], // Request all facets from Algolia
          transformItems: (items) => items,
          widgets: [
            // Price range slider
            (container) => customRangeSlider({ container, attribute: "price" }),

            // Core facets
            (container) =>
              createRefinementListWithHeader({
                container,
                attribute: "collections",
                label:
                  window.FacetMetadata?.getFacetTranslation("collections") ||
                  window.searchTranslations?.filters?.collections ||
                  "Collections",
                searchable: true,
              }),
            (container) =>
              createRefinementListWithHeader({
                container,
                attribute: "vendor",
                label:
                  window.FacetMetadata?.getFacetTranslation("manufacturer") ||
                  window.searchTranslations?.filters?.manufacturer ||
                  "Manufacturer",
              }),
            (container) =>
              createRefinementListWithHeader({
                container,
                attribute: "product_type",
                label:
                  window.FacetMetadata?.getFacetTranslation("productType") ||
                  window.searchTranslations?.filters?.productType ||
                  "Product Type",
              }),

            // Special facets
            (container) =>
              createExpressTagRefinement({
                container,
                attribute: "meta.custom.express_tag",
                label:
                  window.FacetMetadata?.getFacetTranslation("expressShipping") ||
                  window.searchTranslations?.filters?.expressShipping ||
                  "Express Shipping",
              }),
            (container) =>
              createHierarchicalMenuWithHeader({
                container,
                attribute: "meta.custom.item_categories",
                label: window.FacetMetadata?.getFacetTranslation("kategorien") || "Kategorien",
                limit: 1000,
                showMore: true,
                showMoreLimit: 2000,
              }),

            // Generate widgets for all weitere_informationen attributes
            ...(window.FacetMetadata
              ? Object.entries(window.FacetMetadata.FACET_METADATA)
                  .filter(([key]) => key.startsWith("meta.custom.weitere_informationen"))
                  .map(([attribute, metadata]) => (container) => {
                    const label = window.FacetMetadata.getFacetTranslation(metadata.translationKey);

                    if (metadata.type === "grouped_numeric") {
                      return createGroupedNumericRefinementList({
                        container,
                        attribute,
                        label,
                      });
                    }

                    return createRefinementListWithHeader({
                      container,
                      attribute,
                      label,
                    });
                  })
              : []),
          ],
          // No fallbackWidget needed - we've defined all known facets
        }),
      );

      // Initialize search and apply widgets
      widgets.forEach((widget) => search.addWidget(widget));

      // Initialize search
      search.start();
      window.__instantSearchInstance = search;

      // Open first five facets after search initialization
      search.on("render", () => {
        openFirstFiveFacets();
      });

      return search;
    } catch (error) {
      logError(error, "createSearchInstance");
      throw error;
    }
  };

  const initWithRetry = async (initializeSearch, maxRetries = 3, delay = 1000, attempt = 1) => {
    try {
      return await initializeSearch();
    } catch (error) {
      if (attempt >= maxRetries) {
        logError(error, `initWithRetry (attempt ${attempt})`);
        throw error;
      }

      await new Promise((resolve) => {
        setTimeout(resolve, delay);
      });
      return initWithRetry(initializeSearch, maxRetries, delay * 1.5, attempt + 1);
    }
  };

  const setInstantSearchUiState = (indexUiState) => {
    if (!ns.search) return;

    ns.search.setUiState((prevState) => ({
      ...prevState,
      [window.algoliaConfig.indexName]: {
        ...prevState[window.algoliaConfig.indexName],
        ...indexUiState,
      },
    }));
  };

  // Export functions
  Object.assign(ns, {
    createSearchInstance,
    initWithRetry,
    setInstantSearchUiState,
  });
})(window.ProductSearch || (window.ProductSearch = {}));
